import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { device } from '../../utils/styled-component-mixins';
import Accordion from '../shared/Accordion';
import { withLocalization } from '../shared/Context/LocalizationContext';
import { useLocalization } from '../custom-hooks/useLocalization';
import FormSelect from '../shared/FormComponents/FormSelect';

const GlobalEquityMatrixStyled = styled.div`
  .accordion-item {
    &:last-child {
      margin-bottom: 5rem;
    }

    @media ${device.desktop} {
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  button:not(.btn) {
    font-size: 17px;
  }

  .award-type-category-container {
    & > h3 {
      padding: 1rem;
      margin-bottom: 3rem;

      @media ${device.desktop} {
        padding: 4rem 0rem;
        margin-bottom: 0;
      }
    }
  }

  button:not(.btn) {
    border-style: none;
    border-top: 2px solid var(--grey);
    padding: 2.8rem 1.5rem;
    width: 100%;
    transition: all 0.3s ease-in-out;

    &[aria-expanded='false'] {
      background-color: var(--white);

      &:hover {
        background-color: var(--light-grey-2);
      }
    }

    > div {
      align-items: center;
      display: flex;
      justify-content: space-between;

      > span:first-child {
        text-align: left;
        white-space: normal;
      }

      .material-icons {
        color: var(--charcoal-2);
        font-size: 3rem;
      }
    }
  }

  h3 {
    font-size: 2rem;
    font-weight: 800;
    line-height: 2.4rem;

    @media ${device.desktop} {
      font-size: 1.7rem;
      line-height: 2.7rem;
      &.category-title {
        font-size: 2.2rem;
        line-height: 3.2rem;
      }
    }
  }

  .equity-btn {
    white-space: normal;
    width: 100%;
    justify-content: center;
  }

  .cta {
    border: none;
  }

  .element-container {
    padding: 1rem 2rem;
  }

  fieldset {
    border: none;
    margin: 0;
    padding: 0;
  }

  .matrix-app {
    position: relative;
    z-index: 10;
  }

  .matrix-data-container {
    isolation: isolate;
    margin: 0;
    margin-bottom: 4rem;
    padding: 0;
    position: relative;
    z-index: 1;

    @media ${device.desktop} {
      padding-right: 3rem;
    }

    & > h2 {
      font-weight: bold;
      padding: 1rem;
    }
  }

  .matrix-data-card {
    background-color: var(--white);
    box-shadow: var(--card-shadow);
    padding: 1.5rem;

    @media ${device.desktop} {
      padding: 3rem;
    }
  }

  .matrix-header {
    font-weight: bold;
    display: none;

    @media ${device.desktop} {
      display: flex;
    }
  }

  .matrix-wrapper {
    flex-direction: column-reverse;
    margin: 0;
    margin-bottom: 1rem;
    padding: 0;

    @media ${device.desktop} {
      flex-direction: row;
    }
  }

  .matrix-selection-area-container {
    isolation: isolate;
    margin: 0;
    margin-bottom: 4rem;
    padding: 0;
    position: relative;
    z-index: 1;

    @media ${device.desktop} {
      padding: 0;
      padding-left: 3rem;
    }

    .selection-header {
      color: var(--bdo-red);
      font-weight: bold;
    }

    .selection-subheader {
      font-weight: bold;
    }

    .select-dropdown-container {
      display: flex;
      flex: 1 1 100%;
      flex-direction: column;
      justify-content: left;
      padding: 3rem 0;
      z-index: 2;

      @media ${device.desktop} {
        flex-direction: row;
        padding: 2rem 0rem;
      }

      & select {
        width: 100%;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }
  }

  .selection-wrapper {
    background-color: var(--white);
    box-shadow: var(--card-shadow);
    padding: 1.5rem;

    @media ${device.desktop} {
      padding: 2rem;
    }
  }

  .has-content {
    color: var(--browser-outline-blue);
  }

  .non-bdo-style-list {
    z-index: 101;
  }

  .select-selected {
    & > button {
      white-space: normal;
      width: 100%;
    }
  }

  .side-bar-padding {
    @media ${device.desktop} {
      padding-right: 4rem;
    }
  }

  .spacing-wrapper {
    padding: 1rem 0rem;

    @media ${device.desktop} {
      padding: 1rem 0rem;
    }
  }
`;
const GlobalEquityMatrixComponent = (props) => {
  const [countries, setCountries] = useState([]);
  const [awardTypes, setAwardTypes] = useState([]);
  const [taxConsiderations, setTaxConsiderations] = useState([]);
  const [content, setContent] = useState([]);
  const [selectedCountryGuid, setSelectedCountryGuid] = useState({});
  const [selectedAwardTypeGuid, setSelectedAwardTypeGuid] = useState({});
  const [selectedTaxConsiderationGuid, setSelectedTaxConsiderationGuid] = useState({});
  const {
    countryGuid,
    awardTypeGuid,
    taxConsiderationGuid,
    selectionHeader,
    selectionSubHeader,
    selectionOptionsDescription,
    sidebarContent,
    matrixContent
  } = props;
  const selectedCountryObject = countries.find(
    (obj) => obj.CountryGuid === selectedCountryGuid || obj.CountryGuid === countryGuid
  );
  const selectedAwardTypeObject = awardTypes.find(
    (obj) => obj.AwardTypeGuid === selectedAwardTypeGuid || obj.awardTypeGuid === awardTypeGuid
  );
  const selectedTaxConsiderationObject = taxConsiderations.find(
    (obj) =>
      obj.TaxConsiderationGuid === selectedTaxConsiderationGuid ||
      obj.TaxConsiderationGuid === taxConsiderationGuid
  );
  const { resourceStrings } = useLocalization();
  const defaultCountry = resourceStrings['bdO.GlobalEquityMatrix.DefaultCountry'];
  const defaultAwardType = resourceStrings['bdO.GlobalEquityMatrix.DefaultAwardType'];
  const defaultTaxConsideration = resourceStrings['bdO.GlobalEquityMatrix.DefaultTaxConsideration'];

  useEffect(() => {
    fetch(`/api/v1/countries`)
      .then((response) => response.json())
      .then((data) => {
        const options = [{ CountryName: defaultCountry, CountryGuid: '' }].concat(data);
        setCountries(options);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCountryObject) {
      fetch(`api/v1/awardTypes?CountryGUID=${selectedCountryObject.CountryGuid}`)
        .then((response) => response.json())
        .then((data) => {
          const options = [{ AwardTypeName: 'All Award Types', AwardTypeGuid: '' }].concat(data);
          setAwardTypes(options);
        });
    }
  }, [selectedCountryObject]);

  useEffect(() => {
    if (
      selectedCountryObject &&
      selectedAwardTypeObject &&
      selectedAwardTypeObject.AwardTypeGuid !== ''
    ) {
      fetch(
        `api/v1/TaxConsiderations?CountryGUID=${selectedCountryObject.CountryGuid}&AwardTypeGuid=${selectedAwardTypeObject.AwardTypeGuid}`
      )
        .then((response) => response.json())
        .then((data) => {
          const options = [
            { TaxConsiderationName: 'All Tax Considerations', TaxConsiderationGuid: '' }
          ].concat(data);
          setTaxConsiderations(options);
        });
    } else if (selectedCountryObject) {
      let apiOptions = [];
      awardTypes.forEach((awardType) => {
        fetch(
          `api/v1/TaxConsiderations?CountryGUID=${selectedCountryObject.CountryGuid}&AwardTypeGuid=${awardType.AwardTypeGuid}`
        )
          .then((response) => response.json())
          .then((data) => {
            apiOptions = apiOptions.concat(data);
            apiOptions = apiOptions.filter(
              (v, i, a) => a.findIndex((v2) => JSON.stringify(v2) === JSON.stringify(v)) === i
            );
            const options = [
              { TaxConsiderationName: 'All Tax Considerations', TaxConsiderationGuid: '' }
            ].concat(apiOptions);
            setTaxConsiderations(options);
          });
      });
    }
  }, [selectedCountryObject, selectedAwardTypeObject, awardTypes]);

  useEffect(() => {
    const awardTypeParam =
      selectedAwardTypeObject && selectedAwardTypeObject.AwardTypeGuid !== ''
        ? `&AwardTypeGuid=${selectedAwardTypeObject.AwardTypeGuid}`
        : '';
    const taxConsiderationParam =
      selectedTaxConsiderationObject && selectedTaxConsiderationObject.TaxConsiderationGuid !== ''
        ? `&TaxConsiderationGuid=${selectedTaxConsiderationObject.TaxConsiderationGuid}`
        : '';
    if (selectedCountryObject) {
      fetch(
        `api/v1/Content?CountryGUID=${selectedCountryObject.CountryGuid}${awardTypeParam}${taxConsiderationParam}`
      )
        .then((response) => response.json())
        .then((data) => {
          setContent(data);
        });
    }
  }, [selectedCountryObject, selectedAwardTypeObject, selectedTaxConsiderationObject]);

  return (
    <GlobalEquityMatrixStyled className="matrix-app">
      <div className="row matrix-wrapper">
        <div className="matrix-data-container col-lg-8 col-md-12">
          <div className="matrix-data-card">
            <>
              {content.length > 0 ? (
                <div>
                  {content.map((awardType) => (
                    <div key={`${awardType.category}`} className="award-types-container">
                      {awardType.categories && (
                        <div className="award-type-category-container">
                          <h3 className="category-title">{awardType.category}</h3>
                          {awardType.categories.map((taxConsideration, index) =>
                            taxConsideration.categories ? (
                              <Accordion
                                key={`${taxConsideration.category}`}
                                {...taxConsideration}
                                index={index}
                                pageTitle={taxConsideration.category}
                                pageSubTitle={taxConsideration.hasContent}
                                pageSubTitleClass="has-content"
                                contentPadding="3rem 1rem"
                                defaultOpen="true"
                                className="accordion"
                                filledBg={false}
                              >
                                <>
                                  <div className="tax-considerations-container">
                                    {taxConsideration.categories && (
                                      <div>
                                        {taxConsideration.categories.map((element) => (
                                          <div
                                            key={`${element.category}${index}`}
                                            className="element-container"
                                          >
                                            <span>
                                              <strong>{element.category}</strong>
                                            </span>
                                            <br />
                                            <span
                                              dangerouslySetInnerHTML={{ __html: element.content }}
                                            ></span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </>
                              </Accordion>
                            ) : (
                              <div
                                key={`${awardType.category}-${index}`}
                                className="accordion-item"
                              >
                                <h3>
                                  <button
                                    disabled={true}
                                    aria-expanded="false"
                                    aria-controls={`accordion-content-${index}`}
                                    id={`accordion-item-${index}-id`}
                                  >
                                    <div>
                                      <span>
                                        {taxConsideration.category}
                                        {': '}
                                        <span className="has-content">
                                          {taxConsideration.hasContent}
                                        </span>
                                      </span>
                                    </div>
                                  </button>
                                </h3>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: matrixContent }}></div>
              )}
            </>
          </div>
        </div>
        <div className="col-lg-4 col-md-12 matrix-selection-area-container">
          <div className="selection-wrapper">
            <h4>
              <span className="selection-header">{selectionHeader}</span>
            </h4>
            <span className="selection-subheader">{selectionSubHeader}</span>
            <fieldset>
              <div className="select-dropdown-container">
                <FormSelect
                  onChangeFunction={({ target: { value } }) => setSelectedCountryGuid(value)}
                  options={countries.map((country) => ({
                    value: country.CountryGuid,
                    name: country.CountryName
                  }))}
                  selectedOption={selectedCountryGuid}
                  label={defaultCountry}
                  selectId="country-select"
                />
              </div>
            </fieldset>
            {selectedCountryObject && (
              <div>
                <span>{selectionOptionsDescription}</span>
                <fieldset>
                  <div className="select-dropdown-container">
                    <FormSelect
                      onChangeFunction={({ target: { value } }) => setSelectedAwardTypeGuid(value)}
                      options={awardTypes.map((awardType) => ({
                        value: awardType.AwardTypeGuid,
                        name: awardType.AwardTypeName
                      }))}
                      selectedOption={selectedAwardTypeGuid}
                      label={defaultAwardType}
                      selectId="award-type-select"
                    />
                  </div>
                  <div className="select-dropdown-container">
                    <FormSelect
                      onChangeFunction={({ target: { value } }) =>
                        setSelectedTaxConsiderationGuid(value)
                      }
                      options={taxConsiderations.map((taxConsideration) => ({
                        value: taxConsideration.TaxConsiderationGuid,
                        name: taxConsideration.TaxConsiderationName
                      }))}
                      selectedOption={selectedTaxConsiderationGuid}
                      label={defaultTaxConsideration}
                      selectId="tax-consideration-select"
                    />
                  </div>
                </fieldset>
              </div>
            )}
            <div dangerouslySetInnerHTML={{ __html: sidebarContent }}></div>
          </div>
        </div>
      </div>
    </GlobalEquityMatrixStyled>
  );
};
export default withLocalization(GlobalEquityMatrixComponent);
